import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import SupportLink from "@/components/SupportLink";
import FeedbackButton from "@/components/FeedbackButton";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import ReferralModal from "@/components/ReferralModal";
import Seo from "@/components/Seo";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Viewer from "@/pages/Viewer";
import Quiz from "@/pages/Quiz";

import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Circle from "@/pages/Circle";
import Legal from "@/pages/Legal";
import Contact from "@/pages/Contact";
import AdminDashboard from "@/pages/AdminDashboard";
import ToolPdfToFlashcards from "@/pages/ToolPdfToFlashcards";
import ToolTextbookSummarizer from "@/pages/ToolTextbookSummarizer";
import UseCaseMedicalStudents from "@/pages/UseCaseMedicalStudents";
import SharedDeck from "@/pages/SharedDeck";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/AdminBlog";

function AppRouter() {
  const location = useLocation();
  // Capture a referral code (?ref=CODE) on any entry page and remember it until signup.
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("ref");
    if (code) localStorage.setItem("loom_ref", code);
  }, [location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Seo />
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<Legal kind="terms" />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/refund" element={<Legal kind="refund" />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/tools/pdf-to-flashcards" element={<ToolPdfToFlashcards />} />
      <Route path="/tools/textbook-summarizer" element={<ToolTextbookSummarizer />} />
      <Route path="/use-cases/medical-students" element={<UseCaseMedicalStudents />} />
      <Route path="/shared/:shareId" element={<SharedDeck />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/admin/blog" element={<AdminBlog />} />
      <Route path="/explore" element={<Navigate to="/blog" replace />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/circle" element={<ProtectedRoute><Circle /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/deck/:id" element={<Viewer />} />
      <Route path="/quiz/:id" element={<Quiz />} />
      </Routes>
    </>
  );
}

function FloatingHelp() {
  const { pathname } = useLocation();
  // Hide floating pills on the immersive Viewer/Quiz pages so they don't crowd
  // the deck minimap (especially on mobile).
  const immersive = pathname.startsWith("/deck/") || pathname.startsWith("/quiz/");
  if (immersive) return null;
  return (
    <>
      <SupportLink />
      <FeedbackButton />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <FloatingHelp />
          <CookieConsent />
          <ReferralModal />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
